
import { FormCloudProvisionerTemplate, FormCloudProvisionerTemplateResponse, GlobalResponse } from '~/models/Commons ';
import { api } from '../api';
import { CLOUD_PROVISIONER } from '../endpoints';
import { SET_CLOUDPROVISIONER } from '~/store/Provisioner';
import { SET_CLOUDPROVISIONERTEMPLATE } from '~/store/feature/CloudProvisionerTemplate';

const extendedApiSlice = api.injectEndpoints({
  endpoints: (builder) => ({
    getCloudProvisioner: builder.query<GlobalResponse<any>, number>({
      query: (id) => ({
        url: `${CLOUD_PROVISIONER}/${id}/componentLimits`,
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: sessionStorage.getItem('access_token')
            ? `Bearer ${sessionStorage.getItem('access_token')}`
            : '',
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(SET_CLOUDPROVISIONER(data.payload));
        } catch (error) {
          console.error('Error fetching cloud provisioner:', error);
        }
      },
    }),

    getCloudProvisionerFull: builder.query<GlobalResponse<any[]>, void>({
      query: () => ({
        // This query feeds the Provisioning Template dropdown (VM Template form), so it must
        // return ALL templates. The backend paginates with a default size of 10, so without an
        // explicit large size newer templates (e.g. AWS) fall outside the first page.
        url: `${CLOUD_PROVISIONER}?page=0&size=1000`,
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: sessionStorage.getItem('access_token')
            ? `Bearer ${sessionStorage.getItem('access_token')}`
            : '',
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(SET_CLOUDPROVISIONER(data.payload));
        } catch (error) {
          console.error('Error fetching full cloud provisioner list:', error);
        }
      },
    }),

    getCloudProvisionerTemplates: builder.query<
      GlobalResponse<{ content: FormCloudProvisionerTemplateResponse[]; totalElements: number }>,
      { page: number; size: number }
    >({
      query: ({ page, size }) => ({
        url: `${CLOUD_PROVISIONER}?page=${page}&size=${size}`,
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: sessionStorage.getItem('access_token')
            ? `Bearer ${sessionStorage.getItem('access_token')}`
            : '',
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(SET_CLOUDPROVISIONERTEMPLATE(data.payload.content));
        } catch (error) {
          console.error('Error fetching cloud provisioner templates:', error);
        }
      },
    }),

    saveCloudProvisionerTemplate: builder.mutation<
      GlobalResponse<FormCloudProvisionerTemplateResponse>,
      FormCloudProvisionerTemplate
    >({
      query: (data) => ({
        url: CLOUD_PROVISIONER,
        method: 'POST',
        body: data,
        credentials: 'include',
        headers: {
          Authorization: sessionStorage.getItem('access_token')
            ? `Bearer ${sessionStorage.getItem('access_token')}`
            : '',
          'Content-Type': 'application/json',
        },
      }),
    }),

    updateCloudProvisionerTemplate: builder.mutation<
      GlobalResponse<null>,
      { id: number; data: FormCloudProvisionerTemplate }
    >({
      query: ({ id, data }) => ({
        url: `${CLOUD_PROVISIONER}/${id}`,
        method: 'PUT',
        body: data,
        credentials: 'include',
        headers: {
          Authorization: sessionStorage.getItem('access_token')
            ? `Bearer ${sessionStorage.getItem('access_token')}`
            : '',
          'Content-Type': 'application/json',
        },
      }),
    }),

    deleteCloudProvisionerTemplate: builder.mutation<GlobalResponse<null>, number>({
      query: (id) => ({
        url: `${CLOUD_PROVISIONER}/${id}`,
        method: 'DELETE',
        credentials: 'include',
        headers: {
          Authorization: sessionStorage.getItem('access_token')
            ? `Bearer ${sessionStorage.getItem('access_token')}`
            : '',
        },
      }),
    }),
  }),
});

export const {
  useGetCloudProvisionerQuery,
  useLazyGetCloudProvisionerQuery,
  useGetCloudProvisionerFullQuery,
  useLazyGetCloudProvisionerFullQuery,
  useGetCloudProvisionerTemplatesQuery,
  useLazyGetCloudProvisionerTemplatesQuery,
  useSaveCloudProvisionerTemplateMutation,
  useUpdateCloudProvisionerTemplateMutation,
  useDeleteCloudProvisionerTemplateMutation,
} = extendedApiSlice;
